import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;
    const isEditorialProductTabs =
      isDesktop && this.closest('.product-information--editorial-pdp .accordion[data-editorial-tabs-ready]');

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    if (isEditorialProductTabs) {
      if (this.details.open) {
        event.preventDefault();
        return;
      }

      requestAnimationFrame(() => {
        if (!this.details.open) return;

        const accordion = this.closest('.accordion');
        accordion?.querySelectorAll('accordion-custom').forEach((accordionItem) => {
          if (accordionItem === this) return;

          const details = accordionItem.querySelector('details');
          if (details instanceof HTMLDetailsElement) {
            details.open = false;
          }
        });
      });
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

// The desktop PDP shows details as stacked accordions (DESIGN.md, blueprint §11.5).
// Set to true to bring back the horizontal tabs added on 2026-08-06.
const EDITORIAL_TABS_ENABLED = false;

let editorialTabsId = 0;

/**
 * @param {ParentNode} root
 */
function teardownEditorialProductTabs(root = document) {
  root.querySelectorAll('.product-information--editorial-pdp .accordion[data-editorial-tabs-ready]').forEach((accordion) => {
    accordion.querySelector('.pdp-accordion-tabs')?.remove();
    delete accordion.dataset.editorialTabsReady;
  });
}

/**
 * @param {Element} accordion
 */
function syncEditorialProductTabs(accordion) {
  const rows = Array.from(accordion.querySelectorAll(':scope > accordion-custom'));
  const buttons = Array.from(accordion.querySelectorAll('.pdp-accordion-tab'));
  const tabList = accordion.querySelector('.pdp-accordion-tabs');
  const indicator = accordion.querySelector('.pdp-accordion-tabs__indicator');
  let selectedButton = null;

  rows.forEach((row, index) => {
    const details = row.querySelector('details');
    const button = buttons[index];

    if (!(details instanceof HTMLDetailsElement) || !(button instanceof HTMLButtonElement)) return;

    const isOpen = details.open;
    button.setAttribute('aria-selected', String(isOpen));
    button.setAttribute('tabindex', isOpen ? '0' : '-1');

    if (isOpen) {
      selectedButton = button;
    }
  });

  if (!(tabList instanceof HTMLElement) || !(indicator instanceof HTMLElement) || !(selectedButton instanceof HTMLElement)) {
    return;
  }

  const tabListRect = tabList.getBoundingClientRect();
  const buttonRect = selectedButton.getBoundingClientRect();
  const indicatorX = Math.round(buttonRect.left - tabListRect.left);
  const indicatorWidth = Math.round(buttonRect.width);

  tabList.style.setProperty('--pdp-tabs-indicator-x', `${indicatorX}px`);
  tabList.style.setProperty('--pdp-tabs-indicator-width', `${indicatorWidth}px`);
}

/**
 * @param {Element} accordion
 */
function setEditorialProductTabsPanelHeight(accordion) {
  const rows = Array.from(accordion.querySelectorAll(':scope > accordion-custom'));
  const currentOpenRow = rows.find((row) => {
    const details = row.querySelector('details');
    return details instanceof HTMLDetailsElement && details.open;
  });
  let maxHeight = 0;

  accordion.style.removeProperty('--pdp-accordion-panel-min-height');

  rows.forEach((row) => {
    const details = row.querySelector('details');
    const content = row.querySelector('.details-content');

    if (!(details instanceof HTMLDetailsElement) || !(content instanceof HTMLElement)) return;

    details.open = true;
    maxHeight = Math.max(maxHeight, Math.ceil(content.getBoundingClientRect().height));
  });

  rows.forEach((row, index) => {
    const details = row.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) return;

    details.open = currentOpenRow ? row === currentOpenRow : index === 0;
  });

  if (maxHeight > 0) {
    accordion.style.setProperty('--pdp-accordion-panel-min-height', `${maxHeight}px`);
  }
}

/**
 * @param {ParentNode} root
 */
function initEditorialProductTabs(root = document) {
  if (!EDITORIAL_TABS_ENABLED || isMobileBreakpoint()) {
    teardownEditorialProductTabs(root);
    return;
  }

  root.querySelectorAll('.product-information--editorial-pdp .accordion:not([data-editorial-tabs-ready])').forEach((accordion) => {
    const rows = Array.from(accordion.querySelectorAll(':scope > accordion-custom'));
    if (rows.length < 2) return;

    const tabList = document.createElement('div');
    tabList.className = 'pdp-accordion-tabs';
    tabList.setAttribute('role', 'tablist');

    const instanceId = editorialTabsId++;

    rows.forEach((row, index) => {
      const details = row.querySelector('details');
      const summary = row.querySelector('summary');
      const content = row.querySelector('.details-content');

      if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement) || !(content instanceof HTMLElement)) {
        return;
      }

      const button = document.createElement('button');
      const tabId = `pdp-accordion-tab-${instanceId}-${index}`;
      const panelId = content.id || `pdp-accordion-panel-${instanceId}-${index}`;

      content.id = panelId;
      content.setAttribute('role', 'tabpanel');
      content.setAttribute('aria-labelledby', tabId);

      button.type = 'button';
      button.id = tabId;
      button.className = 'pdp-accordion-tab';
      button.textContent = summary.textContent.trim().replace(/\s+/g, ' ');
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panelId);
      button.addEventListener('click', () => {
        rows.forEach((targetRow) => {
          const targetDetails = targetRow.querySelector('details');

          if (targetDetails instanceof HTMLDetailsElement) {
            targetDetails.open = targetRow === row;
          }
        });

        syncEditorialProductTabs(accordion);
      });

      tabList.append(button);
    });

    if (!tabList.children.length) return;

    const indicator = document.createElement('span');
    indicator.className = 'pdp-accordion-tabs__indicator';
    indicator.setAttribute('aria-hidden', 'true');
    tabList.append(indicator);

    const hasOpenDetails = rows.some((row) => {
      const details = row.querySelector('details');
      return details instanceof HTMLDetailsElement && details.open;
    });

    if (!hasOpenDetails) {
      const firstDetails = rows[0]?.querySelector('details');
      if (firstDetails instanceof HTMLDetailsElement) {
        firstDetails.open = true;
      }
    }

    accordion.prepend(tabList);
    accordion.dataset.editorialTabsReady = 'true';
    setEditorialProductTabsPanelHeight(accordion);
    syncEditorialProductTabs(accordion);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initEditorialProductTabs();
    },
    { once: true }
  );
} else {
  initEditorialProductTabs();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initEditorialProductTabs(event.target);
  }
});

mediaQueryLarge.addEventListener('change', () => {
  if (isMobileBreakpoint()) {
    teardownEditorialProductTabs();
    return;
  }

  initEditorialProductTabs();
});

window.addEventListener('resize', () => {
  if (isMobileBreakpoint()) return;

  document.querySelectorAll('.product-information--editorial-pdp .accordion[data-editorial-tabs-ready]').forEach((accordion) => {
    setEditorialProductTabsPanelHeight(accordion);
    syncEditorialProductTabs(accordion);
  });
});
